import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PL_API_CONFIG, PlAPIConfig } from 'pl-core-services';
import {
  Session,
  Event,
  SessionObjective,
  EventSession,
  ClientNote,
} from '@common/models';
import { DateTime, Duration, ToISOTimeOptions } from 'luxon';

export interface ServerSessionObjective {
  accuracy: number;
  correct: number;
  incorrect: number;
  objective: string;
  objective_description: string;
  uuid: string;
}
interface ServerSession {
  attendance_status: string;
  note?: string;
  service_type?: string;
  session_duration?: string;
  session_objectives: ServerSessionObjective[];
  session_status?: string;
  therapy_type?: string;
  uuid?: string;
  id?: string;
}
export interface ServerEventSession {
  id: string;
  uuid: string;
  title: string;
  description?: string;
  sessions: ServerSession[];
  start_date: string;
  end_date: string;
  start_date_local: string;
  end_date_local: string;
  timezone: string;
}

// TODO: Create a module only for services
@Injectable({ providedIn: 'root' })
export class PlSessionService {
  private readonly baseUrl: string;

  constructor(
    @Inject(PL_API_CONFIG) config: PlAPIConfig,
    private http: HttpClient
  ) {
    this.baseUrl = `${config.kanga}/api/v1`;
  }

  calculateSessionDuration(sessionDuration: string): number {
    const hours = Duration.fromISOTime(sessionDuration).hours;
    const minutes = Duration.fromISOTime(sessionDuration).minutes;
    return hours * 60 + minutes;
  }

  formatEventSession(item: ServerEventSession): EventSession {
    const [session] = item.sessions;
    const startDate = DateTime.fromISO(item.start_date);
    return {
      event_uuid: item.id,
      session_uuid: session?.uuid,
      start_date: startDate,
      start_time: startDate.toLocaleString(DateTime.TIME_SIMPLE),
      end_date: DateTime.fromISO(item.end_date),
      title: item.title,
      session_duration: session?.session_duration
        ? this.calculateSessionDuration(session.session_duration)
        : null,
      therapy_type: session?.therapy_type,
      service_type: session?.service_type,
      attendance_status: session?.attendance_status,
      objectives: session?.session_objectives
        ? this.formatSessionObjectives(session?.session_objectives)
        : null,
      notes: session?.note,
    };
  }

  formatSessionObjectives(
    sessionObjectives: ServerSessionObjective[]
  ): SessionObjective[] {
    return sessionObjectives.map((sessionObjective) => ({
      ...sessionObjective,
      accuracy: Number.isFinite(sessionObjective.accuracy)
        ? Math.round(sessionObjective.accuracy)
        : null,
    }));
  }

  getClientSessions(
    clientUuid: string,
    { start, end, iepId }: { start: DateTime; end: DateTime; iepId?: string }
  ): Observable<EventSession[]> {
    const params: any = {
      start: start.toISO({ suppressSeconds: true }).replace('+', '%2B'),
      end: end.toISO({ suppressSeconds: true }).replace('+', '%2B'),
    };

    if (iepId) {
      params.iep__uuid = iepId;
    }

    const url = `${this.baseUrl}/clients/${clientUuid}/events`;
    return this.http
      .get<ServerEventSession[]>(url, {
        params,
      })
      .pipe(
        map((res: ServerEventSession[]): EventSession[] =>
          res.map((r) => this.formatEventSession(r))
        )
      );
  }

  // GET /api/v1/clients/{client_uuid}/events/?has_sessions=true&recurrence_set={recurrence_uuid}&start={start_date_local}
  updateMultipleNotes(eventUuid: string, sessionNotes: ClientNote[]) {
    const url = `${this.baseUrl}/events/${eventUuid}/sessions/`;
    return this.http.post<ServerEventSession[]>(url, {
      session_notes: sessionNotes,
    });
  }

  // GET /api/v1/clients/{client_uuid}/events/?has_sessions=true&recurrence_set={recurrence_uuid}&start={start_date}
  getEventClientSessions(eventUuid: string, clientUuid: string): Promise<any> {
    const url = `${this.baseUrl}/sessions/`;
    const params = {
      client__uuid: clientUuid,
      event__uuid: eventUuid,
    };
    return firstValueFrom(this.http.get(url, { params }));
  }

  getRecurrenceEventClientSessions(
    clientUuid: string,
    recurrenceSetUuid: string,
    startDate: DateTime
  ): Promise<any> {
    const params = {
      has_sessions: true,
      client__uuid: clientUuid,
      recurrence_set: recurrenceSetUuid,
    };
    const start = startDate
      .toISO({ suppressSeconds: true })
      .replace('+', '%2B');
    const url = `${this.baseUrl}/clients/${clientUuid}/events/?start=${start}`;
    return firstValueFrom(this.http.get(url, { params }));
  }

  getSessionDetails(clientUuid: string, objectiveUuid: string) {
    const url = `${this.baseUrl}/clients/${clientUuid}/objectives/${objectiveUuid}/session_details/`;
    return this.http.get<any>(url);
  }

  saveSessionObjective(
    sessionUuid: string,
    objective: SessionObjective
  ): Observable<SessionObjective> {
    const url = `${this.baseUrl}/sessions/${sessionUuid}/objectives/`;
    return this.http.post<SessionObjective>(url, objective);
  }

  formatObjectives(objectives: SessionObjective[]): SessionObjective[] {
    if (!objectives?.length) {
      return [];
    }
    return objectives
      .filter((o) => !!o.objective)
      .map(({ uuid, objective, accuracy }, idx) => ({
        objective,
        uuid: uuid || undefined,
        correct: accuracy || 0,
        incorrect: accuracy ? 100 - accuracy : 0,
        sort: idx,
      }));
  }

  clientEventToServerEvent(event: Event): Partial<ServerEventSession> {
    const toISOTimeOptions: ToISOTimeOptions = {
      includeOffset: false,
      suppressMilliseconds: true,
      suppressSeconds: true,
    };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {
      uuid,
      start_date_local: startDate,
      end_date_local: endDate,
      title,
    } = event;
    return {
      uuid,
      start_date_local: startDate.toISO(toISOTimeOptions),
      timezone: startDate.zoneName,
      end_date_local: endDate.toISO(toISOTimeOptions),
      title,
    };
  }

  addStudentSession(
    clientUuid: string,
    event: Event,
    session: Session,
    objectives: SessionObjective[]
  ): Observable<EventSession> {
    const url = `${this.baseUrl}/clients/${clientUuid}/events/`;
    const payload = {
      ...this.clientEventToServerEvent(event),
      session,
      session_objectives: this.formatObjectives(objectives),
    };
    return this.http
      .post<ServerEventSession>(url, payload)
      .pipe(
        map(
          (res: ServerEventSession): EventSession =>
            this.formatEventSession(res)
        )
      );
  }

  editStudentSession(
    clientUuid: string,
    eventUuid: string,
    event: Event,
    session: Session,
    objectives: SessionObjective[]
  ): Observable<EventSession> {
    const url = `${this.baseUrl}/clients/${clientUuid}/events/${eventUuid}`;
    const serverEvent = this.clientEventToServerEvent(event);
    delete serverEvent.uuid;
    const payload = {
      ...serverEvent,
      session: {
        uuid: session.uuid,
        note: session.note,
        session_duration: session.session_duration,
        attendance_status: session.attendance_status,
      },
      session_objectives: this.formatObjectives(objectives),
    };
    return this.http
      .put<ServerEventSession>(url, payload)
      .pipe(
        map(
          (res: ServerEventSession): EventSession =>
            this.formatEventSession(res)
        )
      );
  }

  deleteStudentSession(clientUuid: string, eventUuid: string) {
    const url = `${this.baseUrl}/clients/${clientUuid}/events/${eventUuid}/`;
    return this.http.delete(url);
  }

  deleteSessionObjective(sessioneUuid: string, sessionObjectiveUuid: string) {
    const url = `${this.baseUrl}/sessions/${sessioneUuid}/objectives/${sessionObjectiveUuid}`;
    return this.http.delete(url);
  }
}
